// Tiny fetch wrapper that always sends credentials, parses JSON, and throws
// an ApiError shaped to match the api's error envelope.

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '');

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }
}

export interface ApiOptions extends Omit<RequestInit, 'body' | 'headers'> {
  body?: unknown;
  headers?: Record<string, string>;
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const { body, headers = {}, ...rest } = options;
  const url = `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

  const init: RequestInit = {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    ...rest,
  };

  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error';
    throw new ApiError(0, { code: 'NETWORK', message });
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const errBody = extractError(payload, response.status);
    throw new ApiError(response.status, errBody);
  }

  return payload as T;
}

function extractError(payload: unknown, status: number): ApiErrorBody {
  if (
    payload &&
    typeof payload === 'object' &&
    'error' in payload &&
    payload.error &&
    typeof payload.error === 'object'
  ) {
    const errorObj = payload.error as Partial<ApiErrorBody>;
    return {
      code: typeof errorObj.code === 'string' ? errorObj.code : 'UNKNOWN',
      message: typeof errorObj.message === 'string' ? errorObj.message : `HTTP ${status}`,
      details: errorObj.details,
    };
  }
  return { code: 'UNKNOWN', message: typeof payload === 'string' ? payload : `HTTP ${status}` };
}
